export interface ShortcutDefinition {
  key: string;
  mod?: boolean;
  shift?: boolean;
  alt?: boolean;
}

export type Shortcut = ShortcutDefinition | ShortcutDefinition[];

export const isTextInputTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

export const isMacPlatform = (): boolean =>
  navigator.platform.toUpperCase().indexOf('MAC') >= 0;

const normalizeKey = (key: string): string => (key.length === 1 ? key.toLowerCase() : key);

const matchesDefinition = (
  event: KeyboardEvent,
  shortcut: ShortcutDefinition,
  isMac: boolean
): boolean => {
  const eventKey = normalizeKey(event.key);
  const shortcutKey = normalizeKey(shortcut.key);
  if (eventKey !== shortcutKey) return false;

  const modPressed = isMac ? event.metaKey : event.ctrlKey;
  if (shortcut.mod === true && !modPressed) return false;
  if (shortcut.mod === false && modPressed) return false;

  if (shortcut.shift === true && !event.shiftKey) return false;
  if (shortcut.shift === false && event.shiftKey) return false;

  if (shortcut.alt === true && !event.altKey) return false;
  if (shortcut.alt === false && event.altKey) return false;

  return true;
};

export const matchesShortcut = (
  event: KeyboardEvent,
  shortcut: Shortcut,
  isMac: boolean
): boolean => {
  if (Array.isArray(shortcut)) {
    return shortcut.some((entry) => matchesDefinition(event, entry, isMac));
  }
  return matchesDefinition(event, shortcut, isMac);
};

export interface ShortcutAction {
  shortcut: Shortcut;
  action: (event: KeyboardEvent) => void;
  preventDefault?: boolean;
  when?: (event: KeyboardEvent) => boolean;
}

export const handleShortcutActions = (
  event: KeyboardEvent,
  actions: ShortcutAction[],
  isMac: boolean
): boolean => {
  for (const { shortcut, action, preventDefault = false, when } of actions) {
    if (!matchesShortcut(event, shortcut, isMac)) continue;
    if (when && !when(event)) {
      continue;
    }
    if (preventDefault) {
      event.preventDefault();
    }
    action(event);
    return true;
  }
  return false;
};

export const DRAW_SHORTCUTS = {
  undo: { key: 'z', mod: true },
  redo: [
    { key: 'z', mod: true, shift: true },
    { key: 'y', mod: true },
  ],
  panHold: { key: ' ' },
  zoomReset: { key: '0', mod: true },
  zoomIn: [
    { key: '=', mod: true },
    { key: '+', mod: true },
  ],
  zoomOut: { key: '-', mod: true },
  toolSelect: { key: 'v' },
  toolPen: { key: 'p' },
  toolEraser: { key: 'e' },
  toolText: { key: 't' },
  toolRectangle: { key: 'r' },
  toolEllipse: { key: 'o' },
  toolDiamond: { key: 'd' },
  toolTriangle: { key: 'g' },
  toolConnector: { key: 'l' },
  toggleGrid: { key: 'g', mod: true },
  toggleSnap: { key: 's' },
  toggleInvert: { key: 'i' },
  showShortcuts: { key: '?' },
};

export const DIAGRAM_SHORTCUTS = {
  undo: { key: 'z', mod: true },
  redo: [
    { key: 'z', mod: true, shift: true },
    { key: 'y', mod: true },
  ],
  delete: [
    { key: 'Delete' },
    { key: 'Backspace' },
  ],
  copy: { key: 'c', mod: true },
  cut: { key: 'x', mod: true },
  paste: { key: 'v', mod: true },
  duplicate: { key: 'd', mod: true },
  selectAll: { key: 'a', mod: true },
  clearSelection: { key: 'Escape' },
  toolSelect: { key: 'v' },
  toolRectangle: { key: 'r' },
  toolEllipse: { key: 'o' },
  toolDiamond: { key: 'd' },
  toolCylinder: { key: 'c' },
  toolConnector: { key: 'a' },
  toolTriangle: { key: 't' },
  panHold: { key: ' ' },
  toggleGrid: { key: 'g' },
  toggleSnap: { key: 's', mod: false, shift: true },
  toggleInvert: { key: 'i' },
  zoomReset: { key: '0', mod: true },
  zoomIn: [
    { key: '=', mod: true },
    { key: '+', mod: true },
  ],
  zoomOut: { key: '-', mod: true },
};

export const APP_SHORTCUTS = {
  save: { key: 's', mod: true },
};
